import { type ElementType, type ReactNode } from "react";
import {
  AccountTree,
  AccessTime,
  Add,
  AddComment,
  ArrowBack,
  ArrowDownward,
  ArrowUpward,
  BookmarkBorder,
  Business,
  Calculate,
  CalendarMonth,
  CameraAlt,
  ChatBubbleOutline,
  Check,
  CheckCircle,
  CheckCircleOutline,
  Checklist,
  Close,
  Comment,
  ContentCopy,
  DeleteOutline,
  Description,
  Download,
  Edit,
  Email,
  ErrorOutline,
  FilterList,
  FolderOpen,
  HelpOutline,
  History,
  Home,
  Info,
  InfoOutlined,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardArrowUp,
  Lightbulb,
  LibraryBooks,
  LocalCafe,
  LocalOffer,
  LocationOn,
  Lock,
  MailOutline,
  Menu,
  MoreHoriz,
  Newspaper,
  NoteAdd,
  NotificationsNone,
  OpenInNew,
  Person,
  Phone,
  Print,
  Refresh,
  Remove,
  RestartAlt,
  Save,
  Search,
  Settings,
  Share,
  ShoppingCart,
  Star,
  SwapVert,
  TrendingUp,
  ViewModule,
  Visibility,
  VisibilityOff,
  WarningAmber,
} from "@mui/icons-material";
import ActiveIcon from "~/common/mui/icons/Active";
import AssemblyIcon from "~/common/mui/icons/Assembly";
import BallBearingIcon from "~/common/mui/icons/BallBearing";
import CadIcon from "~/common/mui/icons/Cad";
import DmcScanIcon from "~/common/mui/icons/DmcScan";
import FileCheckIcon from "~/common/mui/icons/FileCheck";
import FileClockIcon from "~/common/mui/icons/FileClock";
import FileErrorIcon from "~/common/mui/icons/FileError";
import FilePencilIcon from "~/common/mui/icons/FilePencil";
import FlashlightOffIcon from "~/common/mui/icons/FlashlightOff";
import FlashlightOnIcon from "~/common/mui/icons/FlashlightOn";
import PdfIcon from "~/common/mui/icons/Pdf";
import ScreenNewsIcon from "~/common/mui/icons/ScreenNews";
import type { CMSIconCode, CMSIconType } from "~/features/cms/types/icon";

export const CMS_ICON_MAP = {
  Active: ActiveIcon,
  ArrowDown: ArrowDownward,
  ArrowLeft: ArrowBack,
  ArrowUp: ArrowUpward,
  ArrowsUpDown: SwapVert,
  Assembly: AssemblyIcon,
  BallBearing: BallBearingIcon,
  Bell: NotificationsNone,
  Bin: DeleteOutline,
  Bookmark: BookmarkBorder,
  Building: Business,
  Cad: CadIcon,
  Calculator: Calculate,
  Calendar: CalendarMonth,
  Camera: CameraAlt,
  Cart: ShoppingCart,
  CheckCircleOutline,
  CheckCircle,
  Check,
  Checklist,
  ChevronDown: KeyboardArrowDown,
  ChevronLeft: KeyboardArrowLeft,
  ChevronRight: KeyboardArrowRight,
  ChevronUp: KeyboardArrowUp,
  Clock: AccessTime,
  Close,
  Cog: Settings,
  CommentEmpty: ChatBubbleOutline,
  CommentPlus: AddComment,
  Comment,
  CopyToClipboard: ContentCopy,
  Cup: LocalCafe,
  Disk: Save,
  DmcScan: DmcScanIcon,
  Download,
  EnvelopeOpen: MailOutline,
  Envelope: MailOutline,
  ErrorCircle: ErrorOutline,
  EyeOff: VisibilityOff,
  Eye: Visibility,
  FileCheck: FileCheckIcon,
  FileClock: FileClockIcon,
  FileError: FileErrorIcon,
  FileList: Description,
  FilePencil: FilePencilIcon,
  FilePlus: NoteAdd,
  Filter: FilterList,
  FlashlightOff: FlashlightOffIcon,
  FlashlightOn: FlashlightOnIcon,
  FolderOpen,
  History,
  Home,
  InfoCircleOutline: InfoOutlined,
  InformationCircle: Info,
  Invitation: Email,
  Lightbulb,
  List: Menu,
  Location: LocationOn,
  Lock,
  Minus: Remove,
  More: MoreHoriz,
  Newspaper,
  OpenInNew,
  Pages: LibraryBooks,
  Pdf: PdfIcon,
  Pencil: Edit,
  Phone,
  Plus: Add,
  PriceTag: LocalOffer,
  Printer: Print,
  QuestionMarkCircle: HelpOutline,
  Reload: Refresh,
  Reset: RestartAlt,
  ScreenNews: ScreenNewsIcon,
  Search,
  Share,
  Star,
  Tiles: ViewModule,
  TreeView: AccountTree,
  Trending: TrendingUp,
  User: Person,
  Warning: WarningAmber,
} satisfies Record<CMSIconType, ElementType>;

export const resolveCMSIcon = (icon: CMSIconCode | null | undefined): ReactNode | undefined => {
  if (icon == null) {
    return undefined;
  }

  const Icon = Object.prototype.hasOwnProperty.call(CMS_ICON_MAP, icon) ? CMS_ICON_MAP[icon as CMSIconType] : undefined;

  if (!Icon) {
    console.warn(`[CMSColumnLinks] Unsupported CMS icon code "${icon}".`);
    return <HelpOutline data-testid="cms-icon-fallback" data-cms-icon-code={icon} sx={{ fontSize: 24 }} />;
  }

  return <Icon data-cms-icon-code={icon} sx={{ fontSize: 24 }} />;
};
